
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertCircle, Loader, RefreshCw, Info, Shield } from "lucide-react";
import { testWordPressPluginConnection } from "@/utils/wordpressApiExtended";
import { toast } from "@/hooks/use-toast";

export const PluginConnectionStatus = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, any> | null>(null);
  const [diagnostics, setDiagnostics] = useState<{
    apiEndpoint: boolean | null;
    authentication: boolean | null;
    permissions: boolean | null;
    phpVersion: boolean | null;
    wordpressVersion: boolean | null;
  }>({
    apiEndpoint: null,
    authentication: null,
    permissions: null,
    phpVersion: null,
    wordpressVersion: null,
  });
  
  // Check connection status
  const checkConnection = async () => {
    setIsChecking(true);
    
    try {
      // This is a demo, so we're simulating the test
      // In a real app, this would actually connect to WordPress
      const connected = await testWordPressPluginConnection({
        siteId: "4",
        apiKey: "demo_api_key_for_testing",
        siteUrl: "https://www.drive-expert.com"
      });
      
      setIsConnected(connected);
      setLastChecked(new Date().toLocaleString());
      
      // Generate fake diagnostic results
      // In a real app, these would be actual test results
      setTimeout(() => {
        const allPassed = connected;
        
        if (connected) {
          setDiagnostics({
            apiEndpoint: true,
            authentication: true,
            permissions: true,
            phpVersion: true,
            wordpressVersion: true,
          });
          
          setDetails({
            wordpressVersion: "6.4.1",
            phpVersion: "8.1.2",
            pluginVersion: "1.0.0",
            siteUrl: "https://www.drive-expert.com",
            backupCapability: "Enabled",
            incrementalBackup: "Enabled",
            securityScan: "Enabled"
          });
          
          toast({
            title: "Connection Successful",
            description: "Plugin is properly installed and connected"
          });
        } else {
          // Simulate random failures for demo
          setDiagnostics({
            apiEndpoint: false,
            authentication: false,
            permissions: Math.random() > 0.5,
            phpVersion: true,
            wordpressVersion: true,
          });
          
          toast({
            title: "Connection Failed",
            description: "Plugin connection issues detected",
            variant: "destructive"
          });
        }
      }, 1000);
      
    } catch (error) {
      console.error("Connection check failed:", error);
      setIsConnected(false);
      
      toast({
        title: "Connection Error",
        description: "Failed to check plugin connection",
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  };
  
  // Check connection on mount
  useEffect(() => {
    checkConnection();
  }, []);
  
  // Component for diagnostic status
  const DiagnosticItem = ({ 
    label, 
    status, 
    description 
  }: { 
    label: string; 
    status: boolean | null;
    description: string;
  }) => (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0">
      <div>
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
      <div>
        {status === null && <Loader className="h-5 w-5 text-gray-400 animate-spin" />}
        {status === true && <CheckCircle className="h-5 w-5 text-green-500" />}
        {status === false && <XCircle className="h-5 w-5 text-red-500" />}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex justify-between items-center">
          <span>WordPress Plugin Connection</span>
          <div className="flex items-center">
            {isConnected === true && (
              <div className="text-sm text-green-600 flex items-center">
                <CheckCircle className="mr-1 h-4 w-4" />
                Connected
              </div>
            )}
            {isConnected === false && (
              <div className="text-sm text-red-600 flex items-center">
                <XCircle className="mr-1 h-4 w-4" />
                Disconnected
              </div>
            )}
            {isConnected === null && (
              <div className="text-sm text-amber-600 flex items-center">
                <AlertCircle className="mr-1 h-4 w-4" />
                Unknown
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isConnected === false && (
            <div className="p-4 border border-red-200 rounded-md bg-red-50">
              <h3 className="text-sm font-semibold text-red-800 flex items-center mb-2">
                <AlertCircle className="h-4 w-4 mr-2" />
                Connection Issues Detected
              </h3>
              <p className="text-sm text-red-700 mb-4">
                The WordPress plugin is not connecting correctly. Please check the diagnostics below
                to identify and fix the problem.
              </p>
              
              <div className="space-y-1 text-sm">
                <p className="text-red-700"><strong>Common causes:</strong></p>
                <ul className="list-disc pl-5 text-red-700">
                  <li>Plugin not installed or activated</li>
                  <li>Incorrect API key or Site ID entered</li>
                  <li>WordPress REST API disabled</li>
                  <li>Server firewall blocking connections</li>
                </ul>
              </div>
            </div>
          )}
          
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="text-sm font-medium mb-3">Connection Diagnostics</h3>
              <div className="border rounded-md divide-y">
                <DiagnosticItem 
                  label="API Endpoint" 
                  status={diagnostics.apiEndpoint}
                  description="WordPress REST API accessibility"
                />
                <DiagnosticItem 
                  label="Authentication" 
                  status={diagnostics.authentication}
                  description="API key validation"
                />
                <DiagnosticItem 
                  label="Permissions" 
                  status={diagnostics.permissions}
                  description="File system access permissions"
                />
                <DiagnosticItem 
                  label="PHP Version" 
                  status={diagnostics.phpVersion}
                  description="Compatible PHP version"
                />
                <DiagnosticItem 
                  label="WordPress Version" 
                  status={diagnostics.wordpressVersion}
                  description="Compatible WordPress version"
                />
              </div>
            </div>
            
            {isConnected && details && (
              <div>
                <h3 className="text-sm font-medium mb-3">Plugin Information</h3>
                <div className="border rounded-md p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div className="text-gray-500">WordPress Version:</div>
                    <div>{details.wordpressVersion}</div>
                    
                    <div className="text-gray-500">PHP Version:</div>
                    <div>{details.phpVersion}</div>
                    
                    <div className="text-gray-500">Plugin Version:</div>
                    <div>{details.pluginVersion}</div>
                    
                    <div className="text-gray-500">Site URL:</div>
                    <div className="truncate">{details.siteUrl}</div>
                    
                    <div className="text-gray-500">Incremental Backup:</div>
                    <div className="flex items-center">
                      <Shield className="h-3 w-3 mr-1 text-green-600" />
                      {details.incrementalBackup}
                    </div>
                    
                    <div className="text-gray-500">Security Scan:</div>
                    <div className="flex items-center">
                      <Shield className="h-3 w-3 mr-1 text-green-600" />
                      {details.securityScan}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center pt-2">
            <div className="text-xs text-gray-500">
              {lastChecked && (
                <div className="flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  Last checked: {lastChecked}
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={checkConnection}
              disabled={isChecking}
            >
              {isChecking ? (
                <>
                  <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Check Connection
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
